import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

import { DataGrid, useDataGridInstance } from "metabase/data-grid";
import type { DatasetColumn, DatasetData } from "metabase-types/api";

import type { DescribeActionFormResponse } from "../../api/types";
import type { TableDataGetColumnSortDirection } from "../use-edit-table-data";
import type { TableRowUpdateHandler } from "../use-table-crud";

import S from "./EditTableDataGrid.module.css";
import { useEditDataDridColumnOptions } from "./use-edit-datagrid-column-options";
import {
  ROW_SELECT_COLUMN_ID,
  useTableColumnRowSelect,
} from "./use-table-column-row-select";
import {
  useRestoreGridFocusOnCellEditEnd,
  useTableEditingCell,
} from "./use-table-editing-cell";

type EditTableDataGridProps = {
  data: DatasetData;
  updateFormDescription?: DescribeActionFormResponse;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  handleRowUpdate: TableRowUpdateHandler;
  onRowExpandClick: (rowIndex: number) => void;
  getColumnSortDirection?: TableDataGetColumnSortDirection;
  onColumnSort?: (field: DatasetColumn) => void;
};

export const EditTableDataGrid = ({
  data,
  updateFormDescription,
  rowSelection,
  onRowSelectionChange,
  onRowExpandClick,
  getColumnSortDirection,
  onColumnSort,
  handleRowUpdate,
}: EditTableDataGridProps) => {
  const { cols, rows } = data;

  const {
    editingCell,
    getCellState,
    handleSelectEditingCell,
    handleCancelEditing,
    handleUpdateEditingCell,
  } = useTableEditingCell({
    data,
    handleRowUpdate,
  });

  const columnsOptions = useEditDataDridColumnOptions({
    cols,
    editingCell,
    updateFormDescription,
    getColumnSortDirection,
    onColumnSort,
    onCancelEditing: handleCancelEditing,
    onValueUpdated: handleUpdateEditingCell,
    getCellState,
  });

  const columnRowSelectOptions = useTableColumnRowSelect({
    onRowEditClick: onRowExpandClick,
  });

  // Shared by both entry points into cell editing: a mouse click
  // (handleBodyCellClick below) and Enter on the keyboard-selected cell
  // (wired to onEditRequested below) — the readonly/already-editing guards
  // must apply identically to both, or a keyboard-only user could open an
  // editor a mouse user is correctly blocked from (WCAG 2.1.1).
  const handleActivateCell = useCallback(
    (rowIndex: number, columnId: string) => {
      if (
        editingCell?.rowIndex === rowIndex &&
        editingCell?.columnId === columnId
      ) {
        return;
      }

      const parameter = updateFormDescription?.parameters.find(
        ({ id }) => id === columnId,
      );
      if (parameter?.readonly) {
        return;
      }

      handleSelectEditingCell(rowIndex, columnId);
    },
    [handleSelectEditingCell, editingCell, updateFormDescription],
  );

  const handleBodyCellClick = useCallback(
    (
      _event: React.MouseEvent<HTMLDivElement>,
      rowIndex: number,
      columnId: string,
    ) => handleActivateCell(rowIndex, columnId),
    [handleActivateCell],
  );

  const columnSizingMap = useMemo(() => {
    return {
      [ROW_SELECT_COLUMN_ID]: 74,
    };
  }, []);

  const columnOrder = useMemo(
    () => [ROW_SELECT_COLUMN_ID, ...cols.map(({ name }) => name)],
    [cols],
  );

  const tableProps = useDataGridInstance({
    data: rows,
    columnsOptions,
    columnOrder,
    columnSizingMap,
    enableRowSelection: true,
    rowSelection,
    onRowSelectionChange,
    columnRowSelectOptions,
    // Without this, the grid's own cells were never reachable by keyboard
    // at all — only the row-select checkboxes (real <input>s) are naturally
    // tabbable, so a keyboard-only user could Tab down the checkbox column
    // but had no way to enter, or edit, the actual data cells (WCAG 2.1.1,
    // 2.4.3). Enabling this makes the grid container itself one Tab stop,
    // auto-selecting the first cell on focus; arrow keys then move that
    // selection, and Enter (below) opens the inline editor on it — the same
    // action a mouse click already triggers via handleBodyCellClick.
    enableSelection: true,
    onEditRequested: handleActivateCell,
  });

  useRestoreGridFocusOnCellEditEnd(editingCell, tableProps.gridRef);

  const stylingProps = useMemo(
    () => ({
      classNames: {
        headerContainer: S.tableHeaderContainer,
        headerCell: S.tableHeaderCell,
        bodyContainer: S.tableBodyContainer,
        bodyCell: S.tableBodyCell,
        row: S.tableRow,
      },

      // Overrides theme constants and default bg
      styles: {
        bodyCell: {
          backgroundColor: undefined,
        },
      },
      theme: {
        cell: {
          backgroundColor: "",
        },
      },
    }),
    [],
  );

  return (
    <DataGrid
      onBodyCellClick={handleBodyCellClick}
      {...tableProps}
      {...stylingProps}
    />
  );
};
